<template>
  <div style="width: 100%; height: 100%">
    <div
      id="map"
      class="map"
      type="light"
      streetview="disabled"
      oncontextmenu="return false"
    >
      <change-map-type-button
        @click="changeViewType"
        :viewIsAerial="isAerial"
      />
    </div>

    <template v-if="drawPolygon">
      <div class="map-buttons">
        <management-button
          class="map-button"
          type="execute"
          :label="$t('UploadFile')"
          @buttonClick="dialog = true"
        >
          <UploadIcon class="icon" />
        </management-button>
        <management-button
          class="map-button"
          type="execute"
          :label="$t('StartOver')"
          :disabled="!drawing"
          @buttonClick="resetPolygon"
        >
          <StartOverSVG class="icon" :class="{ disabled: !drawing }" />
        </management-button>
      </div>

      <v-dialog v-model="dialog" max-width="790" width="530" persistent>
        <v-card class="delete-modal">
          <v-card-title class="headline">{{ $t("UploadFile") }}</v-card-title>

          <v-card-text>
            <v-main>
              <div class="input-field-container" style="margin-bottom: 10px">
                <label>{{ $tc("Type", 1) }}</label>
                <treeselect
                  v-model="typeSelect"
                  :options="typeSelectOptions"
                  :placeholder="null"
                  :searchable="false"
                />
              </div>
              <div class="input-field-container">
                <label>{{ $tc("Projection", 1) }}</label>
                <treeselect
                  v-model="projection"
                  :options="projectionOptions"
                  :load-options="loadProjections"
                  :placeholder="null"
                  disableFuzzyMatching
                />
              </div>

              <drop-file-area
                style="padding-top: 20px"
                ref="dropFileArea"
                :file-types="acceptedFileTypes"
                :disabled="!fileSelectionEnabled"
                @updateFile="dropzoneFile = $event"
                @trashFile="dropzoneFile = null"
              />
            </v-main>
          </v-card-text>

          <v-card-actions style="padding: 8px 32px">
            <div class="flex-grow-1"></div>
            <management-button
              type="cancel"
              :label="$t('Cancel')"
              @buttonClick="cancelForm()"
            />
            <management-button
              type="execute"
              :label="$t('Submit')"
              @buttonClick="submitForm()"
              :disabled="!buttonActive"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { mapActions } from "vuex";
import { managementMapsMixin } from "@/mixins/management/managementMapsMixin";

import { transformExtent } from "ol/proj";
import { GeoJSON, WKT } from "ol/format";
import { Draw } from "ol/interaction.js";
import { Circle, Fill, Icon, Stroke, Style } from "ol/style.js";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import vueDropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

import LimitSVG from "../assets/management/limit.svg";
import StartOverSVG from "../assets/management/start_over.svg";
import ManagementButton from "./Management/ManagementButton";
import UploadIcon from "@/assets/management/upload_file.svg";
import { boundingExtent } from "ol/extent";
import DropFileArea from "@/components/_Molecules/DropFileArea";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature } from "ol";
import { transform } from "ol/proj";
import { Point } from "ol/geom.js";
import ChangeMapTypeButton from "@/components/_Atoms/ChangeMapTypeButton";

export default {
  name: "NewMap",

  components: {
    DropFileArea,
    ManagementButton,
    LimitSVG,
    StartOverSVG,
    UploadIcon,
    vueDropzone,
    ChangeMapTypeButton,
  },

  mixins: [managementMapsMixin],

  props: [
    "dataUploaded",
    "objects",
    "objectGeomLabel",
    "objectUUID",
    "layer",
    "drawPolygon",
    "highlightFeatures",
    "fromImport",
  ],

  data() {
    return {
      map: null,
      roadMap: null,
      features: [],
      vectorSource: null,
      vectorLayer: null,

      draw: "",
      drawing: false,

      dialog: false,
      typeSelect: null,
      typeSelectOptions: [{ id: "shape", label: "Shape" }],
      projection: null,
      projectionOptions: null,
      dropzoneFile: null,

      featureWKT: null,

      highlightLayer: null,
    };
  },

  mounted() {
    this.createDefaultStyle();
    this.createOtherStyles();
    this.createMap();
    this.createAerialMapLayer();
    this.createHighlightLayer();
    this.vectorLayer.setZIndex(4);
    this.highlightLayer.setZIndex(5);

    if (this.drawPolygon) {
      this.addDrawInteraction();
    } else if (this.highlightFeatures) {
      this.handleMapClick();
      this.handleMapPointerMove();

      bus.$on("set-highlight", (feature) => {
        feature.setStyle(this.style);
      });

      bus.$on("remove-highlight", (feature) => {
        feature.setStyle(null);
      });

      bus.$on("highlight-all", (features) => {
        features.forEach((feature) => {
          feature.setStyle(this.style);
        });
      });

      bus.$on("remove-highlight-all", (features) => {
        features.forEach((feature) => {
          feature.setStyle(null);
        });
      });
    }
  },

  computed: {
    buttonActive() {
      return this.typeSelect && this.projection && this.dropzoneFile;
    },

    acceptedFileTypes() {
      return [".zip", ".rar"];
    },

    /**
     * Determines if the drop file area should be enabled ot not.
     */
    fileSelectionEnabled() {
      return this.typeSelect !== undefined && this.projection !== undefined;
    },
  },

  methods: {
    ...mapActions(["GET_LIST", "CREATE_IMPORT_VALIDATION"]),

    createOtherStyles() {
      let conflictStroke = new Stroke({
        color: "#b74f4f",
        width: 1.25,
      });

      let newObjectStroke = new Stroke({
        color: "#4fb77c",
        width: 1.25,
      });

      let conflictFill = new Fill({
        color: "rgba(255,255,255,0.4)",
      });

      let newObjectFill = new Fill({
        color: "rgba(255,255,255,0.4)",
      });

      this.conflictStyle = new Style({
        image: new Circle({
          fill: conflictFill,
          stroke: conflictStroke,
          radius: 5,
        }),
        fill: conflictFill,
        stroke: conflictStroke,
      });

      this.newObjectStyle = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: newObjectStroke,
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: newObjectStroke,
      });

      this.sEB2321 = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: new Stroke({
            color: "#EB2321",
            width: 1.25,
          }),
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: new Stroke({
          color: "#EB2321",
          width: 1.25,
        }),
      });

      this.s0077B2 = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: new Stroke({
            color: "#0077B2",
            width: 1.25,
          }),
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: new Stroke({
          color: "#0077B2",
          width: 1.25,
        }),
      });

      this.sA2CEE2 = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: new Stroke({
            color: "#A2CEE2",
            width: 1.25,
          }),
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: new Stroke({
          color: "#A2CEE2",
          width: 1.25,
        }),
      });

      this.sFFC075 = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: new Stroke({
            color: "#FFC075",
            width: 1.25,
          }),
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: new Stroke({
          color: "#FFC075",
          width: 1.25,
        }),
      });

      this.s9CC5A1 = new Style({
        image: new Circle({
          fill: newObjectFill,
          stroke: new Stroke({
            color: "#9CC5A1",
            width: 1.25,
          }),
          radius: 5,
        }),
        fill: newObjectFill,
        stroke: new Stroke({
          color: "#9CC5A1",
          width: 1.25,
        }),
      });

      this.singleLocationStyle = new Style({
        image: new Icon({
          anchor: [20, 40],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          opacity: 1,
          src: require("@/assets/map_icons/location.png"),
        }),
        zIndex: 2,
      });
    },

    addDrawInteraction() {
      let draw = new Draw({
        source: this.vectorSource,
        type: "Polygon",
        style: this.defaultStyle,
      });

      this.map.addInteraction(draw);
      this.draw = draw;

      draw.on("drawstart", () => {
        this.drawing = true;
      });

      draw.on("drawend", (event) => {
        let feature = event.feature;
        let geoJsonFormatter = new GeoJSON();
        let featureGeoJSON = geoJsonFormatter.writeFeature(feature);
        let wkt = new WKT().writeFeature(
          geoJsonFormatter.readFeature(featureGeoJSON)
        );

        this.$emit("feature-wkt", wkt);
        this.map.removeInteraction(draw);
      });
    },

    handleMapClick() {
      this.map.on("click", (evt) => {
        let feature = this.map.forEachFeatureAtPixel(
          evt.pixel,
          (feature, layer) => {
            return feature;
          }
        );
        if (feature) {
          let featureUUID = feature.attributes.uuid;
          this.$emit("toggle-selected", featureUUID, feature);
        }
      });
    },

    handleMapPointerMove() {
      this.map.on("pointermove", (evt) => {
        let feature = this.map.forEachFeatureAtPixel(
          evt.pixel,
          (feature, layer) => {
            return feature;
          }
        );

        if (feature) {
          this.map.getTargetElement().style.cursor = "pointer";
        } else {
          this.map.getTargetElement().style.cursor = "";
        }
      });
    },

    addFeatures() {
      this.resetFeatures();
      Object.keys(this.objects).forEach((key) => {
        let splittedLabel = this.objectGeomLabel.split(".");
        let output = this.objects[key];
        let uuid = this.objects[key][this.objectUUID];
        splittedLabel.forEach((key2) => {
          output = output[key2];
        });
        this.defineAllCoordinates(output, uuid);
      });

      this.zoomOnFeatures();
      this.defineStyle();
    },

    defineAllCoordinates(featureGeoJSON, uuid) {
      let feature = new GeoJSON().readFeatures(featureGeoJSON, {
        featureProjection: "EPSG:3857",
      });
      if (feature) {
        feature[0].attributes = { uuid: uuid };
        this.features.push(feature[0]);
        this.vectorSource.addFeature(feature[0]);
      }
    },
    
    defineCoordinates(featureGeoJSON, type) {
      let feature = new GeoJSON().readFeatures(featureGeoJSON, {
        featureProjection: "EPSG:3857"
      });

      this.vectorSource.addFeature(feature[0]);

      if (type === 'conflict') {
        feature[0].setStyle(this.conflictStyle);
      }

      if (type === 'newObject') {
        feature[0].setStyle(this.newObjectStyle);
      }

      return feature[0];
    },

    defineCoordinatesTest(featureGeoJSON, type) {
      const features = new GeoJSON().readFeatures(featureGeoJSON, {
        featureProjection: "EPSG:3857",
      });

      if (features && features.length > 0) {
        const existingFeatures = this.vectorSource.getFeatures();
        const existingGeometries = new Set();
        const featuresToAdd = [];
        const duplicatesToRemove = [];

        existingFeatures.forEach((existingFeature) => {
          existingGeometries.add(
            JSON.stringify(existingFeature.getGeometry().getCoordinates())
          );
        });

        features.forEach((feature) => {
          const featureCoordinates = JSON.stringify(
            feature.getGeometry().getCoordinates()
          );
          if (!existingGeometries.has(featureCoordinates)) {
            // Add the feature to the featuresToAdd array and the vector source
            existingGeometries.add(featureCoordinates);
            featuresToAdd.push(feature);
            this.vectorSource.addFeature(feature);
            this.features.push(feature);
          } else {
            // Mark as a duplicate to be removed
            duplicatesToRemove.push(featureCoordinates);
          }
        });

        // Remove duplicates from the vector source and this.features arrays
        duplicatesToRemove.forEach((coordinates) => {
          existingFeatures.forEach((existingFeature) => {
            const existingCoordinates = JSON.stringify(
              existingFeature.getGeometry().getCoordinates()
            );
            if (existingCoordinates === coordinates) {
              this.vectorSource.removeFeature(existingFeature);
              const duplicateIndex = this.features.findIndex(
                (feat) => feat === existingFeature
              );
              if (duplicateIndex !== -1) {
                this.features.splice(duplicateIndex, 1);
              }
            }
          });
        });

        // Set styles for the featuresToAdd based on the type parameter
        featuresToAdd.forEach((feature) => {
          if (type === "conflict") {
            feature.setStyle(this.conflictStyle);
          } else if (type === "newObject") {
            feature.setStyle(this.newObjectStyle);
          } else if (type === "singleLocation") {
            feature.setStyle(this.singleLocationStyle);
          } else if (type === "#EB2321") {
             feature.setStyle(this.sEB2321);
          } else if (type === "#0077B2") {
             feature.setStyle(this.s0077B2);
          } else if (type === "#A2CEE2") {
             feature.setStyle(this.sA2CEE2);
          } else if (type === "#FFC075") {
             feature.setStyle(this.sFFC075);
          } else if (type === "#9CC5A1") {
             feature.setStyle(this.s9CC5A1);
          }
        });

        return featuresToAdd; // Return the added features
      }
    },

    zoomOnFeatures() {
      let extent = this.vectorLayer.getSource().getExtent();
      this.map.getView().fit(extent, this.map.getSize());
    },

    defineStyle() {
      let style;
      switch (this.layer) {
        case "cartographies":
        case "optical_line_terminations":
        case "fiber_distribution_points":
          style = new Style({
            stroke: new Stroke({
              color: [255, 211, 0],
              width: 2,
            }),
            fill: new Fill({
              color: "rgba(255, 255, 0, 0.1)",
            }),
          });
          break;
        case "pathways":
          style = new Style({
            stroke: new Stroke({
              color: [255, 211, 0],
              width: 2,
            }),
          });
          break;
        default:
          style = new Style({
            image: new Circle({
              radius: 5,
              stroke: new Stroke({
                color: [255, 211, 0],
                width: 2,
              }),
            }),
          });
      }
      this.style = style;
    },

    resetFeatures() {
      this.vectorSource.clear();
    },

    resetPolygon() {
      this.draw.finishDrawing();
      this.vectorLayer.getSource().clear();
      this.addDrawInteraction();
      this.drawing = false;
      this.$emit("feature-wkt", null);
    },

    loadProjections({ callback }) {
      let projectionOptions = [];
      this.GET_LIST({
        list_name: "list_projections",
        code: [this.projection],
      }).then(
        (projections) => {
          projections.forEach((projection) => {
            projectionOptions.push({
              id: projection.code,
              label: projection.code + " : " + projection.definition,
            });
          });
          this.projectionOptions = projectionOptions;
          callback();
        },
        (error) => {
          console.error(error);
        }
      );
    },

    cancelForm() {
      this.typeSelect = null;
      this.projection = null;
      this.dropzoneFile = null;
      this.$refs.dropFileArea.trashFile();
      this.dialog = false;
    },

    submitForm() {
      this.loading = true;
      this.vectorLayer.getSource().clear();

      let file = this.dropzoneFile;
      if (!file) {
        return;
      }

      this.CREATE_IMPORT_VALIDATION({
        endpoint: "workspaces",
        data: this.getFormData(file),
      })
        .then((data) => {
          let wkt_json = data[0].wp_geo_json;

          let feature = this.defineCoordinates(wkt_json);

          let geoJsonFormatter = new GeoJSON();
          let featureGeoJSON = geoJsonFormatter.writeFeature(feature);
          this.featureWKT = new WKT().writeFeature(
            geoJsonFormatter.readFeature(featureGeoJSON)
          );
          this.$emit("feature-wkt", this.featureWKT);
          this.centerOnUploadedLimits(feature);
          this.drawing = true;
          this.map.removeInteraction(this.draw);
        })
        .catch((error) => {
          console.log(error);
        });

      this.cancelForm();
      this.loading = false;
    },

    getFormData(file) {
      let formData = new FormData();
      formData.append("file", file);
      formData.append("projection", this.projection);
      formData.append("file_type", this.typeSelect);
      formData.append("get_geom", true);
      return formData;
    },

    /**
     * Center the map on the area uploaded.
     * @param feature - element in which to zoom in
     */
    centerOnUploadedLimits(feature) {
      let geometry = feature.getGeometry();
      let size = this.map.getSize();
      this.map.getView().fit(geometry, size);
    },

    drawConflictsPaginated(conflicts) {
      this.vectorLayer.getSource().clear();
      conflicts.forEach((conflict) => {
        let geo_json = conflict.geoJSON;
        this.defineCoordinates(geo_json, "conflict");
      });

      this.zoomOnFeatures();
    },

    drawNewObjectsPaginated(newObjects) {
      this.vectorLayer.getSource().clear();
      newObjects.forEach((newObject) => {
        let geo_json = newObject.geoJSON;
        this.defineCoordinates(geo_json, "newObject");
      });

      this.zoomOnFeatures();
    },

    drawNewObject(newObject, color) {
      this.vectorLayer.getSource();
      let geo_json = newObject.geojson;
      this.defineCoordinatesTest(geo_json, color);
      this.zoomOnFeatures();
    },

    centerOnWorkspace(boundBox) {
      let a = this.getBoundingExtentFromBoundingBox(boundBox);
      this.map.getView().fit(a, this.map.getSize());
    },

    getBoundingExtentFromBoundingBox(boundingBox) {
      let string = boundingBox.split(" ");
      let minX = string[0].replace("BOX(", "");
      let minY = string[1].split(",")[0];
      let maxX = string[1].split(",")[1];
      let maxY = string[2].replace(")", "");

      let ext = boundingExtent([
        [+minX, +minY],
        [+maxX, +maxY],
      ]);

      return transformExtent(ext, "EPSG:4326", "EPSG:3857");
    },

    createHighlightLayer() {
      this.highlightLayerSource = new VectorSource({ wrapX: false });
      this.highlightLayer = new VectorLayer({
        source: this.highlightLayerSource,
      });
      this.map.addLayer(this.highlightLayer);
    },

    highlightObject(object) {
      this.highlightLayerSource.clear();
      let features = new GeoJSON().readFeatures(object.geoJSON, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];
      let geometryType = feature.getGeometry().getType();
      let highlightStyle = {
        point: {
          radius: 8,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 10,
          strokeColor: [255, 255, 255],
          strokeWidthSecond: 8,
        },
      };
      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.highlightLayerSource.addFeature(feature);
    },

    getHighlightFeatureStyle(type, highlightStyle) {
      switch (type) {
        case "Point":
          return new Style({
            image: new Circle({
              radius: highlightStyle.point.radius,
              stroke: new Stroke({
                color: [0, 0, 0],
                width: highlightStyle.point.strokeWidth,
                zIndex: 1,
              }),
              fill: new Fill({
                color: highlightStyle.point.fillColor,
                zIndex: 1,
              }),
            }),
          });
        case "Polygon":
        case "MultiPolygon":
          return new Style({
            stroke: new Stroke({
              color: [0, 0, 0],
              width: highlightStyle.polygon.strokeWidth,
              zIndex: 1,
            }),
            fill: new Fill({
              color: highlightStyle.polygon.fillColor,
              zIndex: 1,
            }),
          });
        case "LineString":
        case "MultiLineString":
          return [
            new Style({
              stroke: new Stroke({
                color: [0, 0, 0],
                width: highlightStyle.line.strokeWidth,
                zIndex: 1,
              }),
            }),
            new Style({
              stroke: new Stroke({
                color: highlightStyle.line.strokeColor,
                width: highlightStyle.line.strokeWidthSecond,
                zIndex: 1,
              }),
            }),
          ];
      }
    },
  },

  watch: {
    objects() {
      if (this.objects.length > 0) {
        this.addFeatures();
      } else {
        this.resetFeatures();
      }
    },
  },

  destroyed() {
    bus.$off("set-highlight");
    bus.$off("remove-highlight");
    bus.$off("highlight-all");
    bus.$off("remove-highlight-all");
  },
};
</script>

<style scoped>
.map-button {
  width: 120px;
}

.map-button .icon {
  width: 10px;
  margin-right: 10px;
}

.map-button .icon.disabled path {
  fill: #bebec1;
}

#dropzone {
  margin-top: 20px;
  min-height: 100px;
}

.map-tool {
  margin: 85px 0 0 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/NewMap.vue